/**
 * /web/api/transactions React Query hook.
 *
 * Mirrors the public `/api/v2/transactions` schema (see
 * `app/orders/schemas.py:TransactionListItem`). The `reference` column
 * is server-side polymorphic — it can be a string (legacy V1 rows), a
 * dict (V2 rows like `{Product, Quantity, Reference}` for purchases or
 * `{Type, Amount}` for refunds), or null.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface TransactionListItem {
  id: number;
  type: string | null;
  amount: number;
  b_before: number | null;
  b_after: number | null;
  custom_id: string | null;
  date: string | null;
  reference: unknown;
  status: number;
}

export interface TransactionListResponse {
  items: TransactionListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface TransactionsQuery {
  type?: string;
  custom_id?: string;
  date_from?: string;
  date_to?: string;
  offset?: number;
  limit?: number;
}

export type ExportFormat = "csv" | "xlsx";

/**
 * Download the caller's filtered transactions as a file.
 *
 * Goes straight to `fetch` (not the JSON `api` wrapper) because the
 * response is a binary blob, not JSON. The HttpOnly `ns_sid` session
 * cookie rides along via `credentials: "include"`, so the export is
 * authenticated and owner-scoped exactly like the table itself. Pass
 * only the filter fields — pagination is intentionally dropped so the
 * file contains the full filtered history.
 */
export async function downloadTransactions(
  params: Omit<TransactionsQuery, "offset" | "limit">,
  format: ExportFormat,
): Promise<void> {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  search.set("format", format);

  const res = await fetch(
    `/web/api/transactions/export?${search.toString()}`,
    { credentials: "include", headers: { Accept: "*/*" } },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => undefined);
    throw new ApiError(res.status, text);
  }

  const blob = await res.blob();
  // Prefer the server-provided filename (carries the date stamp).
  const cd = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match ? match[1] : `transactions.${format}`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useTransactions(
  params: TransactionsQuery,
  opts?: Omit<
    UseQueryOptions<TransactionListResponse, ApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return useQuery<TransactionListResponse, ApiError>({
    queryKey: ["transactions", params],
    queryFn: () =>
      api.get<TransactionListResponse>(
        `/web/api/transactions${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 30_000,
    ...(opts ?? {}),
  });
}
